import { type FC, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import classNames from 'classnames';
import { useRequest } from 'loti-request';

import type { MonitoredMarket } from 'app/pages/MonitoringPage/types';
import VideogameIcon from 'app/components/VideogameIcon';
import { getBeginDate, getBeginTime } from 'app/components/MatchPreview/utils';
import TierIndicator from 'app/components/TierIndicator';
import SeparatorDot from 'app/components/SeparatorDot';
import Icon from 'app/components/Icon';
import { COMPETITORS_LOGO, COMPETITORS_ORDER } from 'app/config/competitors.config';
import { formatMarketName, getDisplayedMarket } from 'app/utils/markets';
import OpponentAvatar from 'app/components/OpponentAvatar';
import {
  getFormatedTeams,
  monitoredMarketToMarket,
  getFormatedMatch
} from 'app/pages/MonitoringPage/utils';
import DisplayedMarketOdds from 'app/components/MatchPreview/MatchPreviewTeams/DisplayedMarketOdds';
import { gameSlugClassNameMap } from 'app/components/MatchPreview';
import { sortAsModel } from 'app/utils/array';
import { getElapsedDuration, getTimestamp } from 'app/utils/date';
import Winner3WayOdds from 'app/components/MatchPreview/MatchPreviewTeams/Winner3WayOdds';
import thumbIIcon from 'app/icons/thumb.svg';
import arrowBackIcon from 'app/icons/arrow_back.svg';
import { getHashiHeaders, getHashiUrl } from 'app/utils/api';
// import { createSuccessNotification } from 'app/components/Notification';
import createServerErrorNotification from 'app/components/ServerErrorNotification';
import Tooltip from 'app/components/Tooltip';
// import Button from 'app/components/Button';
import { isOnMonitoringPage } from 'app/config/competitors.config.helper';

import style from './style.scss';

interface Props {
  market: MonitoredMarket;
  now: number;
  hidden: boolean;
  competitorsStatus: Competitor[];
}

const MonitoredMarketCard: FC<Props> = ({
  market: monitoredMarket,
  now,
  hidden,
  competitorsStatus
}) => {
  const {
    competitor_markets,
    game,
    market_ref,
    match,
    pandascore_market,
    serie,
    tournament,
    videogame_slug,
    league
  } = monitoredMarket;

  const { t } = useTranslation();

  const isHiddenRef = useRef(hidden);

  // const getHiddenText = () =>
  //   isHiddenRef.current
  //     ? 'monitoring-filters-hide_notification-text'
  //     : 'monitoring-filters-unhide_notification-text';

  const { fetch } = useRequest({
    headers: getHashiHeaders(),
    method: 'PATCH',
    onError: (response, _, params) => {
      createServerErrorNotification(response, params);
    },
    onSuccess: () => {
      isHiddenRef.current = !isHiddenRef.current;
      // createSuccessNotification(
      //   getHiddenText(),
      //   {},
      //   <Button onClick={onToggleHide} className={style.undoButton} variation="empty">
      //     {t('monitoring-filters-hide_notification-undo')}
      //   </Button>
      // );
    }
  });

  const onToggleHide = () => {
    fetch({
      url: getHashiUrl(`/risk_score_views/${market_ref}/${isHiddenRef.current ? 'unhide' : 'hide'}`)
    });
  };

  if (hidden !== isHiddenRef.current) {
    return null;
  }

  const time = match.scheduled_at ? getBeginTime(match.scheduled_at) : undefined;
  const date = match.scheduled_at ? getBeginDate(match.scheduled_at) : undefined;
  const displayedMarket = getDisplayedMarket(pandascore_market.template);

  const marketNames = t('market', { returnObjects: true }) as Record<string, string>;
  const marketName = formatMarketName(
    {
      ...pandascore_market,
      category: '',
      name: '',
      dynamic_line: false,
      participant_id: null,
      is_prebuilt: false
    },
    match,
    undefined,
    marketNames
  ).replaceAll('Over/Under', 'O/U');

  const opponent1 = match.opponents[0].opponent;
  const opponent2 = match.opponents[1].opponent;

  const pandascoreMarket = monitoredMarketToMarket(
    monitoredMarket,
    pandascore_market.selections,
    marketName
  );

  const sortedCompetitorMarkets = sortAsModel(
    competitor_markets,
    COMPETITORS_ORDER,
    competitor => competitor.competitor_slug
  );

  const sortedAndFilteredCompetitorMarkets = sortedCompetitorMarkets.filter(competitorMarket =>
    isOnMonitoringPage(competitorMarket.competitor_slug)
  );

  const displayTeams = displayedMarket !== 'map-over-under';

  const formatTeams = getFormatedTeams([opponent1, opponent2], displayedMarket);
  const formatedMatch = getFormatedMatch(match);

  return (
    <div className={classNames(style.monitoredMarketCard, gameSlugClassNameMap[videogame_slug])}>
      <Link
        to={{ pathname: `/matches/${match.id}${game ? `/${game.id}` : ''}` }}
        className={style.cardLink}
        target="_blank"
      >
        <div className={style.matchInfo}>
          <VideogameIcon slug={videogame_slug} className={style.videogameIcon} size={16} />
          <div className={style.dateContainer}>
            <div className={style.time}>{time}</div>
            <div className={style.date}>{date}</div>
          </div>
          <div className={style.marketAndMatch}>
            <div className={style.market}>
              <span className={style.marketName}>{marketName}</span>
              {game && (
                <>
                  <SeparatorDot size={5} className={style.marketGameSeparator} />
                  <span>{t('monitoring-market-game_number', { gameNumber: game.position })}</span>
                </>
              )}
            </div>
            <div className={style.matchLink}>{match.name}</div>
            <div className={style.serie}>
              {tournament.tier && <TierIndicator tier={tournament.tier} className={style.tier} />}
              <span title={`${league.name} ${serie.full_name} ${tournament.name}`}>
                {league.name} {serie.full_name} {tournament.name}
              </span>
            </div>
          </div>
        </div>
        <div className={style.pandascoreOdds}>
          <div className={style.oddsWithTeams}>
            {displayTeams && <OpponentAvatar opponent={opponent1} size={15} />}
            {pandascoreMarket.template === 'winner-3-way' ? (
              <Winner3WayOdds
                match={{
                  ...formatedMatch,
                  winner_market: {
                    winner_market_template: 'winner-3-way',
                    tie_proba:
                      pandascoreMarket.selections.find(
                        selection => selection.participant_id === null
                      )?.probability ?? undefined
                  }
                }}
                teams={formatTeams(pandascoreMarket)}
                monospace
                noOverround
                containerClassName={style.pandascoreOdds3Way}
                valueClassName={style.pandascoreOdds3WayValue}
              />
            ) : (
              <DisplayedMarketOdds
                match={formatedMatch}
                teams={formatTeams(pandascoreMarket)}
                forceDisplayedMarket={displayedMarket}
                monospace
                compact
                noOverround
              />
            )}
            {displayTeams && <OpponentAvatar opponent={opponent2} size={15} />}
          </div>
          <div className={style.marginAndUpdatedTime}>
            <span>{(pandascore_market.margin * 100).toFixed(1)}%</span>
            <SeparatorDot />
            <span>
              {getElapsedDuration(getTimestamp(pandascore_market.updated_at), now, {
                days: true,
                hours: true,
                minutes: true
              })}
            </span>
          </div>
        </div>
        <ul className={style.competitorsList}>
          {sortedAndFilteredCompetitorMarkets.map(competitor => {
            const competitorMarket = monitoredMarketToMarket(
              monitoredMarket,
              competitor.selections,
              marketName
            );

            const riskScoreClass = classNames(style.riskScore, {
              [style.riskScoreLow]: competitor.risk_score <= 3,
              [style.riskScoreMid]: competitor.risk_score > 3 && competitor.risk_score <= 6,
              [style.riskScoreHigh]: competitor.risk_score > 6 && competitor.risk_score < 10,
              [style.riskScoreVeryHigh]: competitor.risk_score >= 10
            });

            const competitorFormatedMatch = getFormatedMatch(match, competitor.status);

            const isDown =
              competitorsStatus.find(c => c.slug === competitor.competitor_slug)?.status === 'down';

            return (
              <li key={competitor.market_id} className={style.competitorData}>
                <div className={style.riskContainer}>
                  <div className={riskScoreClass}>
                    {competitor.risk_score.toFixed(1)}
                    <span className={style.percent}>%</span>
                  </div>
                  <Icon
                    href={COMPETITORS_LOGO[competitor.competitor_slug]}
                    className={style.competitorLogo}
                  />
                </div>
                <div className={style.oddsWithTeams}>
                  {displayTeams && <OpponentAvatar opponent={opponent1} size={15} />}
                  {competitorMarket.template === 'winner-3-way' ? (
                    <Winner3WayOdds
                      match={{
                        ...competitorFormatedMatch,
                        winner_market: {
                          winner_market_template: 'winner-3-way',
                          tie_proba:
                            competitorMarket.selections.find(
                              selection => selection.participant_id === null
                            )?.probability_with_overround ?? undefined
                        }
                      }}
                      teams={formatTeams(competitorMarket)}
                      monospace
                      noOverround
                      containerClassName={style.competitorOdds3Way}
                      downStatus={isDown}
                    />
                  ) : (
                    <DisplayedMarketOdds
                      match={competitorFormatedMatch}
                      teams={formatTeams(competitorMarket)}
                      forceDisplayedMarket={displayedMarket}
                      monospace
                      noOverround
                      compact
                      downStatus={isDown}
                    />
                  )}

                  {displayTeams && <OpponentAvatar opponent={opponent2} size={15} />}
                </div>
                <div className={style.marginAndUpdatedTime}>
                  <span>{isDown ? '-' : `${competitor.margin}%`}</span>
                  <SeparatorDot />
                  <span>
                    {isDown
                      ? '-'
                      : getElapsedDuration(getTimestamp(competitor.update_received_at), now, {
                          days: true,
                          hours: true,
                          minutes: true
                        })}
                  </span>
                </div>
              </li>
            );
          })}
        </ul>
      </Link>
      <div>
        <Tooltip content={hidden ? t('monitoring-filters-unhide') : t('monitoring-filters-hide')}>
          <button onClick={onToggleHide} type="button" className={style.hideButton}>
            <Icon href={hidden ? arrowBackIcon : thumbIIcon} className={style.hideIcon} />
          </button>
        </Tooltip>
      </div>
    </div>
  );
};

MonitoredMarketCard.displayName = 'MonitoredMarketCard';

export default MonitoredMarketCard;
